/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import {
  HookDeliveryFailureData,
  HookDeliveryFailureData$Outbound,
  HookDeliveryFailureData$outboundSchema,
} from "./hookdeliveryfailuredata.js";
import {
  HookMCPAttributionEntry,
  HookMCPAttributionEntry$Outbound,
  HookMCPAttributionEntry$outboundSchema,
} from "./hookmcpattributionentry.js";
import {
  HookMCPData,
  HookMCPData$Outbound,
  HookMCPData$outboundSchema,
} from "./hookmcpdata.js";
import {
  HookMessageData,
  HookMessageData$Outbound,
  HookMessageData$outboundSchema,
} from "./hookmessagedata.js";
import {
  HookNotificationData,
  HookNotificationData$Outbound,
  HookNotificationData$outboundSchema,
} from "./hooknotificationdata.js";
import {
  HookPromptAttachmentEntry,
  HookPromptAttachmentEntry$Outbound,
  HookPromptAttachmentEntry$outboundSchema,
} from "./hookpromptattachmententry.js";
import {
  HookPromptData,
  HookPromptData$Outbound,
  HookPromptData$outboundSchema,
} from "./hookpromptdata.js";
import {
  HookSkillData,
  HookSkillData$Outbound,
  HookSkillData$outboundSchema,
} from "./hookskilldata.js";
import {
  HookToolCallData,
  HookToolCallData$Outbound,
  HookToolCallData$outboundSchema,
} from "./hooktoolcalldata.js";
import {
  HookUsageData,
  HookUsageData$Outbound,
  HookUsageData$outboundSchema,
} from "./hookusagedata.js";

/**
 * Feature-specific payloads. Hooks populate only the blocks needed for the event.
 */
export type HookIngestData = {
  /**
   * Device-side delivery failure report, sent with event type client.delivery_failed and a required Idempotency-Key so each failure counts once. The speakeasy-hooks binary writes one when an event could not reach the control plane and redelivers it from the offline spool once connectivity returns, so the record describes the original attempt rather than the request that carried it. It is diagnostic only and never counts as agent activity.
   */
  deliveryFailure?: HookDeliveryFailureData | undefined;
  /**
   * MCP feature payload.
   */
  mcp?: HookMCPData | undefined;
  /**
   * Transcript-derived per-request MCP attribution (Claude Stop/SubagentStop).
   */
  mcpAttribution?: Array<HookMCPAttributionEntry> | undefined;
  /**
   * Configured MCP server snapshot captured at session start or configuration change. Transport credentials must be redacted by the sender.
   */
  mcpInventory?: Array<HookMCPData> | undefined;
  /**
   * Whether the sender was able to read the agent's MCP server list for this session. True with an empty mcp_inventory means the agent genuinely has no servers configured; absent or false means the list could not be read (no agent binary, a failed probe) and the inventory says nothing about what the session can reach. Enforcement that treats a missing inventory as proof of absence must consult this first.
   */
  mcpInventoryCollected?: boolean | undefined;
  /**
   * Assistant/user message payload.
   */
  message?: HookMessageData | undefined;
  /**
   * Local agent notification payload.
   */
  notification?: HookNotificationData | undefined;
  /**
   * Prompt feature payload.
   */
  prompt?: HookPromptData | undefined;
  /**
   * Transcript-derived prompt attachment content (Claude Stop/SubagentStop/SessionEnd).
   */
  promptAttachments?: Array<HookPromptAttachmentEntry> | undefined;
  /**
   * Skill activation payload.
   */
  skill?: HookSkillData | undefined;
  /**
   * Tool call feature payload.
   */
  toolCall?: HookToolCallData | undefined;
  /**
   * Token and cost usage payload.
   */
  usage?: HookUsageData | undefined;
};

/** @internal */
export type HookIngestData$Outbound = {
  delivery_failure?: HookDeliveryFailureData$Outbound | undefined;
  mcp?: HookMCPData$Outbound | undefined;
  mcp_attribution?: Array<HookMCPAttributionEntry$Outbound> | undefined;
  mcp_inventory?: Array<HookMCPData$Outbound> | undefined;
  mcp_inventory_collected?: boolean | undefined;
  message?: HookMessageData$Outbound | undefined;
  notification?: HookNotificationData$Outbound | undefined;
  prompt?: HookPromptData$Outbound | undefined;
  prompt_attachments?: Array<HookPromptAttachmentEntry$Outbound> | undefined;
  skill?: HookSkillData$Outbound | undefined;
  tool_call?: HookToolCallData$Outbound | undefined;
  usage?: HookUsageData$Outbound | undefined;
};

/** @internal */
export const HookIngestData$outboundSchema: z.ZodMiniType<
  HookIngestData$Outbound,
  HookIngestData
> = z.pipe(
  z.object({
    deliveryFailure: z.optional(HookDeliveryFailureData$outboundSchema),
    mcp: z.optional(HookMCPData$outboundSchema),
    mcpAttribution: z.optional(z.array(HookMCPAttributionEntry$outboundSchema)),
    mcpInventory: z.optional(z.array(HookMCPData$outboundSchema)),
    mcpInventoryCollected: z.optional(z.boolean()),
    message: z.optional(HookMessageData$outboundSchema),
    notification: z.optional(HookNotificationData$outboundSchema),
    prompt: z.optional(HookPromptData$outboundSchema),
    promptAttachments: z.optional(
      z.array(HookPromptAttachmentEntry$outboundSchema),
    ),
    skill: z.optional(HookSkillData$outboundSchema),
    toolCall: z.optional(HookToolCallData$outboundSchema),
    usage: z.optional(HookUsageData$outboundSchema),
  }),
  z.transform((v) => {
    return remap$(v, {
      deliveryFailure: "delivery_failure",
      mcpAttribution: "mcp_attribution",
      mcpInventory: "mcp_inventory",
      mcpInventoryCollected: "mcp_inventory_collected",
      promptAttachments: "prompt_attachments",
      toolCall: "tool_call",
    });
  }),
);

export function hookIngestDataToJSON(hookIngestData: HookIngestData): string {
  return JSON.stringify(HookIngestData$outboundSchema.parse(hookIngestData));
}
